if (!customElements.get('remove-gift-wrap')) {
  customElements.define(
    'remove-gift-wrap',
    class RemoveGiftWrap extends HTMLAnchorElement {
      constructor() {
        super();
        this.addEventListener('click', this.onClick.bind(this));
      }

      get cartItems() {
        return this.closest('cart-items');
      }

      onClick(event) {
        event.preventDefault();

        // Use the standard cart remove process; the offer checkbox state is
        // derived from cart contents on re-render, so nothing re-adds it.
        const cartItems = this.closest('cart-items');
        cartItems.updateQuantity(this.getAttribute('data-index'), 0);
      }
    }, { extends: 'a' }
  );
}

if (!customElements.get('gift-wrapping')) {
  // Convergence memos, module-scoped so they survive the innerHTML re-renders
  // that recreate the elements. Shopify caps /cart quantities silently (e.g.
  // when the gift variant's stock runs out), so without these an element that
  // keeps seeing a mismatch on reconnect would re-issue the same request in an
  // endless loop. Keyed by target product ID.
  const giftWrapSyncAttempts = new Map();
  const giftWrapSwapAttempts = new Map();

  customElements.define(
    'gift-wrapping',
    class GiftWrapping extends HTMLElement {
      /*
        One element per eligible cart product (rendered by snippets/gift-wrapping.liquid).
        The gift line in the cart carries the hidden line item property
        `_gift_wrap_for` = main product ID, so state is always derived from
        the cart itself — no cart attributes involved.
      */
      constructor() {
        super();

        this.giftVariantId = this.getAttribute('data-gift-variant-id');
        this.giftTitle = this.getAttribute('data-gift-title');
        this.targetProductId = this.getAttribute('data-target-product-id');
        this.targetQuantity = parseInt(this.getAttribute('data-target-quantity')) || 0;
        this.giftLineKey = this.getAttribute('data-gift-line-key');
        this.giftLineVariantId = this.getAttribute('data-gift-line-variant-id');
        this.giftLineQuantity = parseInt(this.getAttribute('data-gift-line-quantity')) || 0;
        this.giftLineLegacy = this.getAttribute('data-gift-line-legacy') === 'true';
        this.isProcessing = false;
        this.pendingSwapToast = false;
      }

      get giftWrapForProperty() {
        return (theme.cartStrings && theme.cartStrings.giftWrapForProperty) || '_gift_wrap_for';
      }

      get selector() {
        return this.querySelector('[data-gift-wrap-toggle]');
      }

      connectedCallback() {
        if (this.selector === null) return;

        // When the offer checkbox is checked or unchecked
        this.selector.addEventListener('change', theme.utils.debounce((event) => {
          event.target.checked ? this.setGiftWrap() : this.removeGiftWrap();
        }, 300));

        if (!this.giftLineKey) {
          giftWrapSyncAttempts.delete(this.targetProductId);
          giftWrapSwapAttempts.delete(this.targetProductId);
          return;
        }

        // Swap when the gift no longer matches the mapped variant (the main
        // product's variant changed, e.g. Ebook -> Hardcover), or adopt a
        // legacy pre-rework gift line by re-adding it with the property.
        const needsSwap = this.giftLineVariantId && this.giftLineVariantId !== this.giftVariantId;
        if (needsSwap || this.giftLineLegacy) {
          if (giftWrapSwapAttempts.get(this.targetProductId) === this.giftVariantId) return;
          giftWrapSwapAttempts.set(this.targetProductId, this.giftVariantId);
          return this.swapGiftWrap(!needsSwap);
        }
        giftWrapSwapAttempts.delete(this.targetProductId);

        // Keep the gift quantity in sync with the main product's quantity
        if (this.targetQuantity > 0 && this.giftLineQuantity !== this.targetQuantity) {
          if (giftWrapSyncAttempts.get(this.targetProductId) === this.targetQuantity) return;
          giftWrapSyncAttempts.set(this.targetProductId, this.targetQuantity);
          return this.updateGiftQuantity(this.targetQuantity);
        }
        giftWrapSyncAttempts.delete(this.targetProductId);
      }

      setGiftWrap() {
        if (this.isProcessing) return;
        this.isProcessing = true;
        this.enableLoading();

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));

        const body = JSON.stringify({
          items: [
            {
              id: this.giftVariantId,
              quantity: Math.max(this.targetQuantity, 1),
              properties: {
                [this.giftWrapForProperty]: this.targetProductId
              }
            }
          ],
          sections: sectionsToBundle
        });

        fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.isProcessing = false;
              this.pendingSwapToast = false;
              this.disableLoading();
              if (this.selector) this.selector.checked = false;
              // Re-sync the UI with the real cart state (e.g. after a failed swap re-add)
              document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
              return;
            }

            const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
            cartJson['sections'] = parsedState['sections'];
            this.isProcessing = false;
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'gift-wrapping', cart: cartJson });

            if (this.pendingSwapToast) {
              this.pendingSwapToast = false;
              if (theme.showCartNotification && theme.cartStrings && theme.cartStrings.giftUpdated) {
                theme.showCartNotification(theme.cartStrings.giftUpdated);
              }
            }
          })
          .catch((error) => {
            this.isProcessing = false;
            this.pendingSwapToast = false;
            this.disableLoading();
            console.error(error);
          });
      }

      removeGiftWrap() {
        if (this.isProcessing) return;
        if (!this.giftLineKey) return;
        this.isProcessing = true;
        this.enableLoading();

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));

        const body = JSON.stringify({
          id: this.giftLineKey,
          quantity: 0,
          sections: sectionsToBundle
        });

        this.fetchGiftWrap(body, theme.routes.cart_change_url);
      }

      updateGiftQuantity(quantity) {
        if (this.isProcessing) return;
        if (!this.giftLineKey) return;
        this.isProcessing = true;
        this.enableLoading();

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));

        const body = JSON.stringify({
          id: this.giftLineKey,
          quantity: quantity,
          sections: sectionsToBundle
        });

        this.fetchGiftWrap(body, theme.routes.cart_change_url);
      }

      swapGiftWrap(silent = false) {
        if (this.isProcessing) return;
        this.isProcessing = true;
        this.enableLoading();

        // Remove the mismatched gift line first, then add the mapped variant.
        const removeBody = JSON.stringify({ id: this.giftLineKey, quantity: 0 });

        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), ...{ body: removeBody } })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.status || parsedState.errors) {
              this.isProcessing = false;
              this.disableLoading();
              // Removal failed (e.g. the line was re-keyed by a concurrent
              // change): abort the swap and re-sync the UI instead of
              // risking a duplicate gift line.
              document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
              return;
            }

            this.isProcessing = false;
            this.giftLineKey = '';
            this.giftLineVariantId = '';
            // The toast fires from setGiftWrap only once the re-add succeeds.
            this.pendingSwapToast = !silent;
            this.setGiftWrap();
          })
          .catch((error) => {
            this.isProcessing = false;
            this.disableLoading();
            console.error(error);
          });
      }

      fetchGiftWrap(body, url) {
        fetch(url || theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            this.isProcessing = false;
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'gift-wrapping',
              cart: parsedState
            });
          })
          .catch((error) => {
            this.isProcessing = false;
            this.disableLoading();
            console.log(error);
          });
      }

      enableLoading() {
        const loader = this.querySelector('.loader');
        if (loader) loader.hidden = false;
      }

      disableLoading() {
        const loader = this.querySelector('.loader');
        if (loader) loader.hidden = true;
      }
    }
  );
}

if (!customElements.get('gift-note')) {
  customElements.define(
    'gift-note',
    class GiftNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
      }

      onChange(event) {
        const body = JSON.stringify({
          attributes: {
            [theme.cartStrings.giftNoteAttribute]: event.target.value
          }
        });
        fetch(theme.routes.cart_update_url, {...theme.utils.fetchConfig(), ...{ body }});
      }
    }
  );
}

if (!customElements.get('gift-wrap-selector')) {
  customElements.define(
    'gift-wrap-selector',
    class GiftWrapSelector extends HTMLElement {
      constructor() {
        super();

        this.giftWrapId = this.getAttribute('data-gift-wrap-id');
        this.lineItemIndex = parseInt(this.getAttribute('data-line-item-index'));
        this.giftItemIndex = parseInt(this.getAttribute('data-gift-item-index'));
      }

      get cartItems() {
        return this.closest('cart-items');
      }

      get lineItem() {
        const item = this.querySelector('script[type="application/json"][data-line-item]')?.textContent;
        return !!item ? JSON.parse(item) : {};
      }

      get giftItem() {
        const item = this.querySelector('script[type="application/json"][data-gift-item]')?.textContent;
        return !!item ? JSON.parse(item) : {};
      }

      connectedCallback() {
        this.addEventListener('change', this.onChange.bind(this));

        if (this.lineItem.properties[theme.cartStrings.giftWrapAttribute] === theme.cartStrings.giftWrapBooleanTrue && !this.giftItem.hasOwnProperty('key')) {
          this.unsetGiftWrap();
        }
      }

      onChange(event) {
        event.preventDefault();
        event.stopPropagation();
        const input = event.target;

        const isChecked = input.type == 'checkbox' ? input.checked : input.value == theme.cartStrings.giftWrapBooleanTrue;
        isChecked ? this.setGiftWrap() : this.unsetGiftWrap();
      }

      setGiftWrap() {
        this.cartItems.enableLoading(this.lineItem.key);

        // Remove target product
        let body = JSON.stringify({
          id: this.lineItem.key,
          quantity: 0
        });
        
        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.errors) {
              theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'gift-wrap', cart: parsedState });
              return;
            }

            // Re-add target product and gift wrapping to cart
            let sectionsToBundle = [];
            document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
            
            const lineItemProps = {
              ...this.lineItem.properties,
              [theme.cartStrings.giftWrapAttribute]: theme.cartStrings.giftWrapBooleanTrue
            };
            let body = JSON.stringify({
              items: [
                {
                  id: this.lineItem.variant_id,
                  quantity: this.lineItem.quantity,
                  properties: lineItemProps
                },
                {
                  id: this.giftWrapId,
                  quantity: this.lineItem.quantity,
                  properties: {
                    [theme.cartStrings.targetProductAttribute]: this.lineItem.title
                  },
                  parent_id: this.lineItem.variant_id
                }
              ],
              sections: sectionsToBundle
            });

            fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
              .then((response) => response.json())
              .then(async (parsedState) => {
                const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET')})).json();
                cartJson['sections'] = parsedState['sections'];
                theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'gift-wrap', cart: parsedState });
              });
          })
          .catch((error) => {
            console.error(error);
          });
      }

      unsetGiftWrap() {
        this.cartItems.enableLoading(this.lineItem.key);

        // Remove target product and gift wrapping
        let updates = {
          [this.lineItem.key]: 0
        };
        if (this.giftItem.hasOwnProperty('key')) {
          updates[this.giftItem.key] = 0;
        }

        let body = JSON.stringify({ updates });

        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.errors) {
              theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'gift-wrap', cart: parsedState });
              return;
            }
            
            // Re-add target product to cart
            let sectionsToBundle = [];
            document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

            const lineItemProps = { ...this.lineItem.properties };
            delete lineItemProps[theme.cartStrings.giftWrapAttribute];

            let body = JSON.stringify({
              id: this.lineItem.variant_id,
              quantity: this.lineItem.quantity,
              properties: lineItemProps,
              sections: sectionsToBundle
            });

            fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
              .then((response) => response.json())
              .then(async (parsedState) => {
                const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET')})).json();
                cartJson['sections'] = parsedState['sections'];
                theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'gift-wrap', cart: cartJson });
              });
          })
          .catch((error) => {
            console.error(error);
          });
      }
    }
  );
}

if (!customElements.get('gift-wrap-item')) {
  customElements.define(
    'gift-wrap-item',
    class GiftWrapItem extends HTMLElement {
      constructor() {
        super();
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get giftSection() {
        return this.closest('.cart__gift');
      }

      get lineItem() {
        const item = this.querySelector('script[type="application/json"][data-line-item]')?.textContent;
        return !!item ? JSON.parse(item) : {};
      }

      get giftItem() {
        const item = this.querySelector('script[type="application/json"][data-gift-item]')?.textContent;
        return !!item ? JSON.parse(item) : {};
      }

      connectedCallback() {
        const quantity = this.lineItem.quantity || 0;
        if (quantity !== this.giftItem.quantity) {
          this.updateGiftWrap(this.giftItem.key, quantity);
        }
      }

      updateGiftWrap(line, quantity) {
        this.enableLoading(line);

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

        let body = JSON.stringify({
          id: line,
          quantity: quantity,
          sections: sectionsToBundle
        });

        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'gift-wrap', cart: parsedState });
          })
          .catch((error) => {
            console.error(error);
          });
      }

      enableLoading(line) {
        this.giftSection.classList.add('pointer-events-none');

        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = false;
      }
    }
  );
}

if (!customElements.get('unset-gift-wrap')) {
  customElements.define(
    'unset-gift-wrap',
    class UnsetGiftWrap extends HTMLAnchorElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick.bind(this));
      }

      get giftWrapItem() {
        return this.closest('gift-wrap-item');
      }

      onClick(event) {
        event.preventDefault();
        this.giftWrapItem.updateGiftWrap(this.getAttribute('data-index'), 0);
      }
    }, { extends: 'a' }
  );
}